import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { camelCase } from 'lodash';

import { FacilityFormType, GridConnectionApplicationType, InstallationFlowType, TechnicianFormType } from '../../constants/app.constants';
import { FormTypesData } from '../../domains/common.domain';
import { Coordinate } from '../../model/coordinate';
import { CommonService } from '../../services/common.service';
import { CommunicationService } from '../../services/communication.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { ErrorService } from '../../services/error/error.service';
import { NetzTranslateService } from '../../services/netz-translate.service';
import { ValidationsService } from '../../services/validations.service';
import { AllDropdownKeys, UrlSegments } from '../jurisdiction.constants';
import {
  CheckServiceAvailabilityData,
  CreateGridCompFeedInApplicationResponse,
  CreateInstallationApplicationResponse,
  FeedInInstallationDataObject,
  LocationDetails,
  LookupOptionsData,
} from '../jurisdiction.domain';
import { JurisdictionService } from '../jurisdiction.service';
import { NavigationStateShareService } from '../../services/navigation-state-share.service';
import {
  GetInstallationApplicationObject,
  GridCompFeedInApplicationData,
  InstallationApplicationData,
} from '../facilities-form/facilities-form.domain';
import {
  CreateGridConnectionRequest,
  CreateGridConnectionResponse,
  FormSubsectionsVisibility,
  GetGridConnectionObject,
  GridConnectionData,
} from '../technician-data/technician-data.domain';
import { TechnicianResponseData } from '../../employee-overview/employee-overview.domain';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { EnvironmentService } from '../../services/environment.service';
import { LocationService } from './location.service';
import { distinctUntilChanged, switchMap, debounceTime, startWith, map } from 'rxjs/operators';
import { Dropdown } from 'primeng';
import { MapOptions } from 'leaflet';
import { DestroyableComponent } from '../../common/destroyable.component';
import { AreaOption } from '../../model/area-option.model';
import { RegistrationOfEnergyProducerApplication } from 'src/app/+regep/entities/regep-application';
import { AppLocation } from '../../+regep/entities/app-location';
import { DrrCcdDeviceService } from '../../+drr-ccd-devices/technical-data/technical-data.service';
import { UserService } from '../../services/user.service';

@Component({
  providers: [ConfirmationService, ValidationsService],
  selector: 'app-location-form',
  templateUrl: './location-form.component.html',
  styleUrls: ['./location-form.component.scss'],
})
export class LocationFormComponent extends DestroyableComponent implements OnInit, AfterViewChecked {
  @ViewChildren('checkboxElement')
  spanElements: QueryList<ElementRef>;

  buildingTypeKey: string = AllDropdownKeys.BuildingType;
  buildingTypes: LookupOptionsData[] = this.commonService.getDropdownData(this.buildingTypeKey);

  feedInTypeKey: string = AllDropdownKeys.FeedInType;
  feedInTypes: LookupOptionsData[] = this.commonService.getDropdownData(this.feedInTypeKey);

  changeRequestTypeKey: string = AllDropdownKeys.ChangeSubtype;
  changeRequestTypes: LookupOptionsData[] = this.commonService.getDropdownData(this.changeRequestTypeKey);

  devAreaKey: string = AllDropdownKeys.DevelopmentAreaName;
  devAreaTypes: LookupOptionsData[] = this.commonService.getDropdownData(this.devAreaKey);

  currentInstallationFlow: string =
    this.localStorageService.getItem('original_flow') || this.localStorageService.getItem('installation_flow') || '';

  disableFormButtons: boolean = true;
  installationFlowType = InstallationFlowType;
  isLoading: boolean = false;
  isNewInstallation: boolean;
  failedJurisdictionChecks: string[] = [];
  mediumVisibility: FormSubsectionsVisibility = {
    gas: false,
    heating: false,
    power: false,
    water: false,
  };
  jurisdictionCheckFailed: boolean = true;
  jurisdictionCheckMessage: string = '';
  locationDataFG: FormGroup;
  selectedFacilityTypes: FormTypesData[] = [];
  showAreaName: boolean = false;
  showDropdowns: boolean;
  isChangeInstallation: boolean;
  isFeedSystem: boolean;
  isChangeConnection: boolean;
  isConversionToGas: boolean;
  isGridCompatibility: boolean;
  isRegEP: boolean;
  isConsumptionDevices: boolean;
  gpsCoordinates: Coordinate;
  loadingGPS: boolean = false;
  technicianId: string;
  postCodes: LookupOptionsData[] = [];

  gasInstallationChangeTypesKey: string = AllDropdownKeys.InstallationChangeTypeGas;
  gasInstallationChangeTypes: LookupOptionsData[] = this.commonService.getDropdownData(this.gasInstallationChangeTypesKey);

  heatingInstallationChangeTypesKey: string = AllDropdownKeys.InstallationChangeTypeHeating;
  heatingInstallationChangeTypes: LookupOptionsData[] = this.commonService.getDropdownData(this.heatingInstallationChangeTypesKey);

  installationChangeTypesKey: string = AllDropdownKeys.InstallationChangeType;
  installationChangeTypes: LookupOptionsData[] = this.commonService.getDropdownData(this.installationChangeTypesKey);

  waterInstallationChangeTypesKey: string = AllDropdownKeys.InstallationChangeTypeWater;
  waterInstallationChangeTypes: LookupOptionsData[] = this.commonService.getDropdownData(this.waterInstallationChangeTypesKey);

  addParcelAddress: boolean = false;

  infoBubblesData: {
    [key: string]: boolean;
  } = this.environmentService.getInfoBubblesData('location') || null;
  options: MapOptions = this.environmentService.mapData.options;
  showOrderMessage: boolean = this.environmentService.showOrderMessage;
  showConversionOptions: boolean = this.environmentService.showConversionOptions;
  showNewDevAreaDetails: boolean = this.environmentService.showNewDevAreaDetails;
  reverseBuildingTypes: boolean = this.environmentService.reverseBuildingTypes;
  showGPS: boolean = this.environmentService.showGPS;
  showParcelNumberTextBox: boolean = this.environmentService.showParcelNumberTextBox;
  showWarningText: boolean = this.environmentService.showWarningText;
  showFeedInSystemJurisdictionCheckLabel: boolean = this.environmentService.showFeedInSystemJurisdictionCheckLabel;
  showDecommissioningInFeedInSystem: boolean = this.environmentService.showDecommissioningInFeedInSystem;
  hideCommodityWater: boolean = this.environmentService.hideCommodityWater;
  hideJurisdictionCheckButton: boolean = this.environmentService.hideJurisdictionCheckButton;
  hideConversionOnHeatingRadioButton: boolean = this.environmentService.hideConversionOnHeatingRadioButton;
  useDropdownForArea: boolean = this.environmentService.useDropdownForArea;
  showApartmentOverSix: boolean = this.environmentService.jurisdictionShowApartmentOverSix;
  showTotalFeedInPowerOfTheEnergyProducer = this.environmentService.showTotalFeedInPowerOfTheEnergyProducer;
  areaOptions: AreaOption[] = [];
  showOrdnanceWarning: boolean = false;
  newBuildingWarning: boolean = false;
  showWaterNote: boolean;
  useAreaLocation: boolean;
  role: string;
  selectedArea: AreaOption;
  showCopyApplicationDataSection: boolean = false;

  locationOptions$: Observable<LocationDetails[]>;
  locationSearchSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  locationDropdown: Dropdown;
  isLocationNotRequiredForType = false;
  isUsePowerConnectionTypeForNewMains = false;
  hideBuildingTypesForNewMains = false;

  private mediaTypeUpdated: boolean = false;
  private updateApplicationKey: string = 'update_application';
  application: any;
  private areaLocations: any;

  get showNewDevAreaDropdownForConnection(): boolean {
    return (
      this.environmentService.showNewDevAreaDropdownForConnection &&
      (this.currentInstallationFlow === this.installationFlowType.MainsConnection ||
        this.currentInstallationFlow === this.installationFlowType.TemporaryConnection)
    );
  }

  get showNewDevAreaDropdownForInstallation(): boolean {
    return (
      this.environmentService.showNewDevAreaDropdownForInstallation &&
      (this.currentInstallationFlow === this.installationFlowType.NewInstallation ||
        this.currentInstallationFlow === this.installationFlowType.UpdateInstallation)
    );
  }

  get isCustomerUser(): boolean {
    return this.userService.isUserCustomerRole();
  }

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private jurisdictionService: JurisdictionService,
    private stateShare: NavigationStateShareService,
    private communicationService: CommunicationService,
    private errorService: ErrorService,
    private netzTranslateService: NetzTranslateService,
    private commonService: CommonService,
    private environmentService: EnvironmentService,
    private cdr: ChangeDetectorRef,
    private locationService: LocationService,
    private drrCcdDeviceService: DrrCcdDeviceService,
    private userService: UserService
  ) {
    super();

    // if coming from copy request- skip removing ids as we need to load and update the data later
    if (!this.isAllowedToUpdateLocationData) {
      this.localStorageService.removeItem('connection_object_id');
      this.localStorageService.removeItem('business_partner_id');
      this.localStorageService.removeItem(this.updateApplicationKey);
    }

    this.registerForLanguageChange();

    if (this.useDropdownForArea) {
      this.environmentService.getAreaOptions.then((options: AreaOption[]) => {
        this.areaOptions = options;
      });
    }
  }

  ngOnInit(isRefresh = false): void {
    this.technicianId = this.localStorageService.getItem('technician_id');
    this.role = this.localStorageService.getItem('role');

    this.isNewInstallation = this.currentInstallationFlow === InstallationFlowType.NewInstallation;
    this.isChangeInstallation = this.currentInstallationFlow === InstallationFlowType.UpdateInstallation;
    this.isFeedSystem = this.currentInstallationFlow === InstallationFlowType.FeedInSystem;
    this.isChangeConnection = this.currentInstallationFlow === InstallationFlowType.ChangeHouseConnection;
    this.isConversionToGas = this.currentInstallationFlow === InstallationFlowType.ConversionToGas;
    this.isGridCompatibility = this.currentInstallationFlow === InstallationFlowType.GridCompatibility;
    this.isRegEP = this.currentInstallationFlow === InstallationFlowType.RegEP;
    this.isConsumptionDevices = this.currentInstallationFlow === InstallationFlowType.ConsumptionDevices;
    this.showDropdowns = this.isChangeInstallation;
    this.showCopyApplicationDataSection = this.isRegEP
      ? this.environmentService.showCopyApplicationSectionForRegep
      : this.isNewInstallation
      ? this.environmentService.showCopyApplicationSectionForNewInstallation
      : this.isChangeInstallation
      ? this.environmentService.showCopyApplicationSectionForChangeConnection
      : false;

    this.showWaterNote =
      this.environmentService.showWaterNote &&
      this.currentInstallationFlow === InstallationFlowType.TemporaryConnection &&
      this.role === 'customer';
    this.commonService.installationApplicationData = null;
    this.commonService.locationData = this.localStorageService.getItem('location_data') || null;
    this.isLocationNotRequiredForType =
      this.environmentService.isAddressNotRequired && this.currentInstallationFlow === this.installationFlowType.MainsConnection;
    this.isUsePowerConnectionTypeForNewMains =
      this.environmentService.usePowerConnectionTypeForNewMains &&
      this.currentInstallationFlow === this.installationFlowType.MainsConnection;

    const applicationId = this.localStorageService.getItem('application_id');
    if (applicationId) {
      this.getExistingApplication(applicationId, this.currentInstallationFlow, isRefresh);
    } else {
      this.prepareForm(isRefresh);
      this.selectLocationForUpdate();

      if (this.commonService.locationData) {
        this.verifyServiceAvailability();
      }
    }
    this.getTechnicianData();

    this.locationOptions$ = this.locationSearchSubject
      .asObservable()
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((searchTerm: string) => (searchTerm.length > 2 ? this.locationService.searchLocations(searchTerm) : of([])))
      )
      .pipe(startWith([]));

    this.hideBuildingTypesForNewMains = this.shouldHideBuildingTypes();
  }

  selectLocation(locationDetails: LocationDetails) {
    this.updateLocationValues(
      { x: locationDetails.position.longitude, y: locationDetails.position.latitude, z: this.gpsCoordinates.z },
      locationDetails
    );
  }

  regEpJurisdictionDataChange({
    totalFeedInPowerIsAboveCentralGridThreshold,
    externalId,
  }: {
    externalId?: string | null;
    totalFeedInPowerIsAboveCentralGridThreshold: boolean;
  }) {
    if (totalFeedInPowerIsAboveCentralGridThreshold) {
      this.locationDataFG.controls.city?.reset();
      this.locationDataFG.controls.postcode?.reset();
      this.locationDataFG.controls.street?.reset();
      this.locationDataFG.controls.street_number?.reset();
      this.locationDataFG.controls.district?.reset();
      this.locationDataFG.controls.location?.reset();
      this.locationDataFG.controls.area?.reset();
      this.locationDataFG.controls.area?.setValue('');
      this.locationDataFG.controls.field?.reset();
      this.locationDataFG.controls.field?.setValue('');
      this.locationDataFG.controls.field_sector?.reset();
      this.locationDataFG.controls.field_sector?.setValue('');
      this.selectedArea = null;
      this.locationDataFG.removeControl('other_area');
    }

    this.locationDataFG.patchValue({
      totalFeedInPowerIsAboveCentralGridThreshold: totalFeedInPowerIsAboveCentralGridThreshold,
      gridCompExternalId: externalId,
    });
  }

  // set location address dropdown, set map location- based on location data in localStorage // this is valid for copy request
  selectLocationForUpdate() {
    if (this.isAllowedToUpdateLocationData && this.isValidLocationData) {
      var searchTerm = `${this.commonService.locationData.street} ${this.commonService.locationData.street_number}, ${this.commonService.locationData.postcode} ${this.commonService.locationData.city}`;
      this.preventLeak(this.locationService.searchLocations(searchTerm)).subscribe(res => {
        if (res && res[0] && res[0].position) {
          this.selectLocation(res[0]);
        }
      });
    }
  }

  ngAfterViewChecked(): void {
    if (this.spanElements && this.spanElements.length && !this.mediaTypeUpdated) {
      this.selectMeter();
    }
  }

  @ViewChild('locationSelect') set locationSelect(element: Dropdown) {
    if (element) {
      this.locationDropdown = element;

      // This is needed since primeng v9 dropdown does not emit an event on filter
      // https://www.primefaces.org/primeng-v9-lts/#/dropdown
      this.locationDropdown.onFilter = (event: any) => {
        this.locationDropdown.filterValue = event.target.value;
        this.locationSearchSubject.next(event.target.value);
      };

      this.locationDropdown.activateFilter = () => (this.locationDropdown.optionsChanged = true);

      this.locationDropdown.findOption = (value: any, options: any[], _?: boolean): SelectItem => {
        let index: number = this.locationDropdown.findOptionIndex(value, options);
        return index !== -1 ? options[index] : this.locationDropdown.selectedOption;
      };
    }
  }

  get showAddress(): boolean {
    return !this.locationDataFG.value?.useAreaLocation;
  }

  get isValidLocationData(): boolean {
    return (
      this.commonService.locationData &&
      this.commonService.locationData.street &&
      this.commonService.locationData.street_number &&
      this.commonService.locationData.postcode &&
      this.commonService.locationData.city
    );
  }
  get isAllowedToUpdateLocationData(): boolean {
    return this.localStorageService.getItem('gridcomp_update_application');
  }

  selectMeter(event?: any, fieldName: string = null) {
    this.jurisdictionCheckFailed = true;
    this.jurisdictionCheckMessage = '';
    if (!event && !fieldName) {
      this.spanElements.forEach(element => {
        const iconElement = element.nativeElement.parentElement.children[0];
        const labelElement = element.nativeElement.parentElement.children[1];
        const field = element.nativeElement.id;

        if (this.locationDataFG.value[field]) {
          iconElement.classList.add('active-icon');
          labelElement.classList.add('active-icon-label');
        } else {
          iconElement.classList.remove('active-icon');
          labelElement.classList.remove('active-icon-label');
        }
      });
      this.mediaTypeUpdated = true;
    } else {
      const element = (event.target && event.target.parentNode) || null;

      if (element) {
        const iconElement = element.children[0];
        const labelElement = element.children[1];

        if (!iconElement.classList.contains('active-icon')) {
          iconElement.classList.add('active-icon');
          labelElement.classList.add('active-icon-label');

          this.locationDataFG.controls[fieldName].setValue(true);
          if (this.currentInstallationFlow === this.installationFlowType.UpdateInstallation) {
            this.locationDataFG.controls[`${fieldName}Type`].setValidators([Validators.required]);
            this.locationDataFG.controls[`${fieldName}Type`].markAsTouched();
            this.locationDataFG.controls[`${fieldName}Type`].updateValueAndValidity();
          }
        } else {
          iconElement.classList.remove('active-icon');
          labelElement.classList.remove('active-icon-label');

          this.locationDataFG.controls[fieldName].setValue(false);
          if (this.currentInstallationFlow === this.installationFlowType.UpdateInstallation) {
            this.locationDataFG.controls[`${fieldName}Type`].reset();
            this.locationDataFG.controls[`${fieldName}Type`].clearValidators();
            this.locationDataFG.controls[`${fieldName}Type`].updateValueAndValidity();
          }
        }
      }
    }
  }

  enableLswButton() {
    this.locationDataFG.valueChanges.subscribe(address => {
      if (
        address.postcode &&
        address.city &&
        address.street &&
        address.street_number &&
        address.singleFamily &&
        (address.power || address.gas || address.water || address.heating)
      ) {
        this.disableFormButtons = false;
      }
    });
  }

  evaluateFormValidity(): void {
    if (this.isFeedSystem || this.isChangeConnection || this.isConversionToGas) {
      this.disableFormButtons = this.locationDataFG.invalid;
      return;
    }

    if (this.currentInstallationFlow === this.installationFlowType.MainsConnection) {
      this.disableFormButtons = !(
        this.locationDataFG.valid &&
        (this.locationDataFG.value.power ||
          this.locationDataFG.value.gas ||
          this.locationDataFG.value.water ||
          this.locationDataFG.value.heating)
      );
      return;
    }

    if (this.isGridCompatibility || this.isRegEP) {
      this.disableFormButtons = this.locationDataFG.value.useAreaLocation
        ? !(this.locationDataFG.value.area && this.locationDataFG.value.field && this.locationDataFG.value.field_sector)
        : !this.locationDataFG.valid;

      return;
    }

    if (this.currentInstallationFlow === InstallationFlowType.ConsumptionDevices) {
      this.disableFormButtons = !this.locationDataFG.valid;
      return;
    }

    this.disableFormButtons = !(
      this.locationDataFG.valid &&
      (this.locationDataFG.value.power ||
        this.locationDataFG.value.gas ||
        this.locationDataFG.value.water ||
        this.locationDataFG.value.heating)
    );
  }

  verifyServiceAvailability(): void {
    this.jurisdictionCheckMessage = '';
    this.failedJurisdictionChecks = [];

    if ((this.isGridCompatibility || this.isRegEP) && this.locationDataFG.value.useAreaLocation) {
      this.showNewDevAreaDetails = true;
      this.addParcelAddress = true;
      this.isLoading = false;

      return;
    }

    const facilityTypesToCheck =
      this.isGridCompatibility || this.isRegEP
        ? ['power']
        : FacilityFormType.filter(
            type =>
              (typeof this.locationDataFG.value[type.key] === 'boolean' && this.locationDataFG.value[type.key]) ||
              (this.isConversionToGas &&
                ((type.index === 1 && this.locationDataFG.controls.conversionType.value === 'conversion_gas') ||
                  (type.index === 3 && this.locationDataFG.controls.conversionType.value === 'conversion_heat'))) ||
              (this.isFeedSystem && type.index === 0)
          ).map(facilityFormType => facilityFormType.key);

    const requests = facilityTypesToCheck.map(facilityType => this.checkJurisdiction(facilityType));

    this.preventLeak(forkJoin(requests)).subscribe(
      jurisdictionChecks => {
        this.failedJurisdictionChecks = jurisdictionChecks
          .filter(check => !check.successfulJurisdictionCheck)
          .map(check => check.facilityType);

        this.jurisdictionCheckFailed = !!this.failedJurisdictionChecks.length;

        this.isLoading = false;
        this.showOrdnanceWarning = jurisdictionChecks.find(check => check.ordnanceArea)?.ordnanceArea;

        this.setJurisdictionCheckMessage();
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  disabledSubmitButton(): boolean {
    if (this.locationDataFG?.value.totalFeedInPowerIsAboveCentralGridThreshold) {
      return !this.locationDataFG?.value.gridCompExternalId;
    }

    if (this.useAreaLocation) {
      if (this.isConsumptionDevices) {
        return (
          !this.locationDataFG.controls.area.value ||
          !this.locationDataFG.controls.field.value ||
          !this.locationDataFG.controls.field_sector.value ||
          !this.isOtherAreaValid ||
          !this.locationDataFG.controls.consumption_devices?.valid
        );
      }

      // todo: add more validation checks
      const isComodityValid =
        // check if any of the comodities is visible
        (this.locationDataFG.controls.power === undefined &&
          this.locationDataFG.controls.gas === undefined &&
          this.locationDataFG.controls.water === undefined &&
          this.locationDataFG.controls.heating === undefined) ||
        // if any is visible, at least 1 must be selected
        this.locationDataFG.controls.power?.value ||
        this.locationDataFG.controls.gas?.value ||
        this.locationDataFG.controls.water?.value ||
        this.locationDataFG.controls.heating?.value;

      return (
        !this.locationDataFG.controls.area.value ||
        !this.locationDataFG.controls.field.value ||
        !this.locationDataFG.controls.field_sector.value ||
        !this.isOtherAreaValid ||
        this.locationDataFG.controls.connectionModification?.invalid ||
        !isComodityValid ||
        this.locationDataFG.controls.powerType?.invalid ||
        this.locationDataFG.controls.conversionType?.invalid ||
        this.locationDataFG.controls.buildingType?.invalid ||
        this.locationDataFG.controls.connectionType?.invalid
      );
    }

    if (this.isConsumptionDevices) {
      return !this.locationDataFG?.valid;
    }

    return this.isChangeConnection ||
      (this.showNewDevAreaDetails && this.addParcelAddress) ||
      (this.hideJurisdictionCheckButton && this.useAreaLocation)
      ? this.disableFormButtons
      : this.jurisdictionCheckFailed;
  }

  updateGPS(coordinates?: Coordinate): void {
    const defaultZoom = 15;
    if (coordinates) {
      this.gpsCoordinates = {
        x: coordinates.x,
        y: coordinates.y,
        z: coordinates.z || defaultZoom,
      };

      return;
    }

    const gpsInput = this.locationDataFG.get('gps').value;
    if (gpsInput && gpsInput.length > 0) {
      const items = gpsInput.split(',');

      this.gpsCoordinates = {
        x: items[0] && items[0].trim(),
        y: items[1] && items[1].trim(),
        z: items[2] ? items[2] && items[2].trim() : defaultZoom,
      };

      return;
    }

    this.gpsCoordinates = { ...this.environmentService.mapData.gpsCoordinates };
  }

  setLocationValues(location: LocationDetails) {
    if (!location) return;

    this.locationDataFG.controls.location?.setValue(location);

    if (this.locationDropdown) {
      this.locationDropdown.filterValue = location.formattedAddress;
      this.locationDropdown.options = [location];
    }

    this.locationDataFG.controls.postcode?.setValue(location.address.postCode);
    this.locationDataFG.controls.city?.setValue(location.address.city);
    this.locationDataFG.controls.district?.setValue(location.address.district);
    this.locationDataFG.controls.street?.setValue(location.address.street);

    if (location.address.houseNumber) {
      this.locationDataFG.controls.street_number?.setValue(location.address.houseNumber);
    }

    this.locationDataFG.controls.gps?.setValue(location.position.longitude.toFixed(2) + ', ' + location.position.latitude.toFixed(2));
    this.updateGPS({ x: location.position.longitude, y: location.position.latitude, z: null });

    this.locationDataFG.markAsTouched();
  }

  updateLocationValues(coordinate: Coordinate, searchLocation: LocationDetails = null): void {
    this.loadingGPS = true;
    this.locationService
      .getLocationDetails(coordinate.y, coordinate.x, this.netzTranslateService.getCurrentLang())
      .subscribe(coordinateLocation => {
        if (searchLocation?.address && searchLocation?.formattedAddress !== coordinateLocation.formattedAddress) {
          coordinateLocation.address = searchLocation?.address;
        }
        coordinateLocation.formattedAddress = searchLocation?.formattedAddress ?? coordinateLocation.formattedAddress;
        this.setLocationValues(coordinateLocation);
        this.loadingGPS = false;
      });
  }

  displayAreaName(event: boolean | any): void {
    this.showAreaName = !!event.checked;
    if (this.showNewDevAreaDropdownForConnection || this.showNewDevAreaDropdownForInstallation) {
      this.locationDataFG.controls.devAreaName.reset();
      if (this.showAreaName) {
        this.locationDataFG.controls.devAreaName.setValidators([Validators.required]);
      } else {
        this.locationDataFG.controls.devAreaName.clearValidators();
      }
      this.locationDataFG.controls.devAreaName.updateValueAndValidity();
      if (this.isLocationNotRequiredForType) {
        this.handleValidators(this.showAreaName);
      }
    }
    this.evaluateFormValidity();
  }

  updateFormSwsValues(event: boolean, reset: boolean = true) {
    this.addParcelAddress = event;
    this.handleValidators(this.addParcelAddress);
    ['swsDevAreaSubDistrict', 'swsParcel', 'swsParcelNumber', 'swsDevAreaName'].forEach(element => {
      if (reset) {
        this.locationDataFG.controls[element].reset();
      }
      if (this.addParcelAddress && element !== 'swsDevAreaName') {
        this.locationDataFG.controls[element].setValidators([Validators.required]);
      } else {
        this.locationDataFG.controls[element].clearValidators();
      }
      this.locationDataFG.controls[element].updateValueAndValidity();
    });
    this.addParcelAddress = false;
  }

  submitFormData(): void {
    this.localStorageService.setItem('original_flow', this.currentInstallationFlow);
    this.currentInstallationFlow =
      this.showConversionOptions && this.currentInstallationFlow === this.installationFlowType.MainsConnection && this.isConversionToGas
        ? InstallationFlowType.ConversionToGas
        : this.currentInstallationFlow;
    this.localStorageService.setItem('installation_flow', this.currentInstallationFlow);

    this.HandleSpecificApplication();
  }

  onSelectingConversion(event: boolean) {
    if (event) {
      this.locationDataFG.removeControl('power');
      this.locationDataFG.removeControl('gas');
      this.locationDataFG.removeControl('water');
      this.locationDataFG.removeControl('heating');
      this.locationDataFG.addControl('conversionType', new FormControl('', [Validators.required]));
    } else {
      this.locationDataFG.addControl('power', new FormControl(false));
      this.locationDataFG.addControl('gas', new FormControl(false));
      this.locationDataFG.addControl('water', new FormControl(false));
      this.locationDataFG.addControl('heating', new FormControl(false));
      this.locationDataFG.removeControl('conversionType');
    }
    this.isConversionToGas = event;
  }

  setFeedInInstType(type: string) {
    this.localStorageService.setItem('feedin_inst_type', type);
  }

  showMediaSelectionBoxes(): boolean {
    return (
      !this.isFeedSystem &&
      !this.isChangeConnection &&
      !this.isConversionToGas &&
      !this.isGridCompatibility &&
      !this.isRegEP &&
      this.currentInstallationFlow !== this.installationFlowType.ConsumptionDevices &&
      this.currentInstallationFlow !== InstallationFlowType.SiteElectricityMeterdata
    );
  }

  toggleAreaFields() {
    if (this.locationDataFG.value.useAreaLocation) {
      this.locationDataFG.controls.city?.reset();
      this.locationDataFG.controls.postcode?.reset();
      this.locationDataFG.controls.street?.reset();
      this.locationDataFG.controls.street_number?.reset();
      this.locationDataFG.controls.district?.reset();
      this.locationDataFG.controls.location?.reset();
      this.useAreaLocation = true;
      this.localStorageService.removeItem('location_data');
    } else {
      this.selectedArea = null;
      this.locationDataFG.controls.area?.reset();
      this.locationDataFG.controls.area?.setValue('');
      this.locationDataFG.controls.field?.reset();
      this.locationDataFG.controls.field?.setValue('');
      this.locationDataFG.controls.field_sector?.reset();
      this.locationDataFG.controls.field_sector?.setValue('');
      this.addOtherAreaField();
      this.useAreaLocation = false;
      this.localStorageService.removeItem('area_locations');
    }
  }

  showJurisdictionCheckButton() {
    return (
      !this.useAreaLocation &&
      (this.showNewDevAreaDropdownForConnection ||
        this.showNewDevAreaDropdownForInstallation ||
        (!this.showNewDevAreaDropdownForConnection && !this.showNewDevAreaDropdownForInstallation && !this.showNewDevAreaDetails) ||
        (this.showNewDevAreaDetails && !this.addParcelAddress)) &&
      !this.isChangeConnection &&
      !(this.hideJurisdictionCheckButton && this.useAreaLocation && (this.isGridCompatibility || this.isRegEP)) &&
      !this.isConsumptionDevices
    );
  }

  selectArea(value: AreaOption) {
    this.locationDataFG.controls.area.setValue(value.area);
    this.locationDataFG.controls.postcode.setValue(value.zip);
    this.setAreaLocation({
      area: this.locationDataFG.controls.area.value,
      postcode: this.locationDataFG.controls.postcode.value,
    });
    this.addOtherAreaField();
  }

  dataCopied() {
    this.ngOnDestroy();
    this.ngOnInit(true);
  }

  get isAddressNotRequired(): boolean {
    return this.isLocationNotRequiredForType && this.showAreaName;
  }

  get isOtherAreaValid(): boolean {
    return this.locationDataFG.controls.other_area == null || this.locationDataFG.controls.other_area.valid;
  }

  get isOtherAreaSelected(): boolean {
    return (
      this.useDropdownForArea &&
      typeof this.locationDataFG.controls.area.value === 'string' &&
      this.locationDataFG.controls.area.value.includes('Sonstige Gemarkung')
    );
  }

  private addOtherAreaField() {
    if (this.isOtherAreaSelected) {
      this.locationDataFG.addControl(
        'other_area',
        new FormControl(null, [Validators.required, ValidationsService.lettersNumbersSpacesValidator()])
      );
      this.locationDataFG.controls.other_area.markAsTouched();
    } else {
      if (this.locationDataFG.controls.other_area) {
        this.locationDataFG.removeControl('other_area');
      }
    }
  }

  private setAreaLocation(value: any) {
    const areaLocations = this.areaLocations ?? [];
    areaLocations[0] = value;
    this.localStorageService.setItem('area_locations', [...areaLocations]);
  }

  private prepareForm(isRefresh: boolean = false): void {
    let gpsData = `${this.environmentService.mapData.gpsCoordinates.x}, ${this.environmentService.mapData.gpsCoordinates.y}`;
    if (this.environmentService.mapData.gpsCoordinates.z !== null && this.environmentService.mapData.gpsCoordinates.z !== undefined) {
      gpsData += `, ${this.environmentService.mapData.gpsCoordinates.z}`;
    }

    this.locationDataFG = new FormGroup({
      location: new FormControl({
        value: this.commonService.locationData?.location,
        disabled: false,
        validators: [Validators.required],
      }),
      postcode: new FormControl({
        value: (this.commonService.locationData && this.commonService.locationData.postcode) || '',
        disabled: false,
      }),
      city: new FormControl({
        value: (this.commonService.locationData && this.commonService.locationData.city) || '',
        disabled: false,
      }),
      street: new FormControl({
        value: (this.commonService.locationData && this.commonService.locationData.street) || '',
        disabled: false,
      }),
      street_number: new FormControl({
        value: (this.commonService.locationData && this.commonService.locationData.street_number) || '',
        disabled: false,
      }),
      gps: new FormControl({
        value: (this.commonService.locationData && this.commonService.locationData.gps) || gpsData,
        disabled: false,
      }),
      power: new FormControl(
        {
          value:
            this.currentInstallationFlow === InstallationFlowType.SiteElectricityMeterdata
              ? true
              : (this.commonService.locationData && this.commonService.locationData.power) || false,
          disabled: false,
        },
        [Validators.required]
      ),
      gas: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.gas) || false,
          disabled: false,
        },
        [Validators.required]
      ),
      water: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.water) || false,
          disabled: false,
        },
        [Validators.required]
      ),
      heating: new FormControl(
        {
          value: (this.commonService.locationData && this.commonService.locationData.heating) || false,
          disabled: false,
        },
        [Validators.required]
      ),
    });

    if (this.environmentService.showDistrict) {
      this.locationDataFG.addControl(
        'district',
        new FormControl({
          value: (this.commonService.locationData && this.commonService.locationData.district) || '',
          disabled: false,
        })
      );
    }

    this.updateGPS();
    this.handleValidators();

    if (this.currentInstallationFlow === this.installationFlowType.UpdateInstallation) {
      this.locationDataFG.addControl('powerType', new FormControl({ value: '', disabled: false }));
      this.locationDataFG.addControl('gasType', new FormControl({ value: '', disabled: false }));
      this.locationDataFG.addControl('waterType', new FormControl({ value: '', disabled: false }));
      this.locationDataFG.addControl('heatingType', new FormControl({ value: '', disabled: false }));
    } else if (this.isFeedSystem) {
      this.locationDataFG.removeControl('power');
      this.locationDataFG.removeControl('gas');
      this.locationDataFG.removeControl('water');
      this.locationDataFG.removeControl('heating');
      this.locationDataFG.addControl('construction-type', new FormControl('', [Validators.required]));
    } else if (this.currentInstallationFlow === this.installationFlowType.TemporaryConnection) {
      this.locationDataFG.addControl('newDevArea', new FormControl({ value: false, disabled: false }));
      this.locationDataFG.addControl('devAreaName', new FormControl({ value: '', disabled: false }));
      this.locationDataFG.removeControl('gas');
      this.locationDataFG.removeControl('heating');
    } else if (this.isChangeConnection) {
      this.locationDataFG.removeControl('power');
      this.locationDataFG.removeControl('gas');
      this.locationDataFG.removeControl('water');
      this.locationDataFG.removeControl('heating');
      this.locationDataFG.addControl('connectionModification', new FormControl('', [Validators.required]));
    } else if (this.isConversionToGas) {
      this.locationDataFG.addControl('conversionType', new FormControl('', [Validators.required]));
      this.locationDataFG.removeControl('power');
      this.locationDataFG.removeControl('gas');
      this.locationDataFG.removeControl('water');
      this.locationDataFG.removeControl('heating');
    } else if (this.isGridCompatibility || this.isRegEP) {
      this.locationDataFG.removeControl('power');
      this.locationDataFG.removeControl('gas');
      this.locationDataFG.removeControl('water');
      this.locationDataFG.removeControl('heating');

      this.locationDataFG.addControl(
        'totalFeedInPowerIsAboveCentralGridThreshold',
        new FormControl(this.commonService.locationData?.totalFeedInPowerIsAboveCentralGridThreshold || false)
      );
    }

    this.configureAreaLocation();

    if (this.locationDataFG.value.useAreaLocation && this.application.connectionobject.area_locations?.length) {
      var firstAreaLocation = this.application.connectionobject.area_locations[0];
      this.locationDataFG.controls.area.setValue(firstAreaLocation.area);
      this.locationDataFG.controls.postcode.setValue(firstAreaLocation.postcode);
      this.locationDataFG.controls.field.setValue(firstAreaLocation.field);
      this.locationDataFG.controls.field_sector.setValue(firstAreaLocation.field_sector);
    }

    if (
      this.currentInstallationFlow === this.installationFlowType.MainsConnection ||
      this.installationFlowType.TemporaryConnection ||
      this.installationFlowType.ConversionToGas
    ) {
      this.locationDataFG.addControl(
        'newDevArea',
        new FormControl({ value: this.commonService.locationData?.newDevArea || false, disabled: false })
      );
      if (this.showNewDevAreaDropdownForConnection || this.showNewDevAreaDropdownForInstallation) {
        this.locationDataFG.addControl(
          'devAreaName',
          new FormControl({ value: this.commonService.locationData?.devAreaName || '', disabled: false })
        );
        this.showAreaName = this.commonService.locationData?.devAreaName || false;
      }
      if (this.showNewDevAreaDetails) {
        this.locationDataFG.addControl(
          'noAddressAvailable',
          new FormControl({
            value: this.commonService.locationData?.noAddressAvailable || false,
            disabled: false,
          })
        );
        this.locationDataFG.addControl(
          'swsDevAreaSubDistrict',
          new FormControl({
            value: this.commonService.locationData?.swsDevAreaSubDistrict || '',
            disabled: false,
          })
        );
        this.locationDataFG.addControl(
          'swsParcel',
          new FormControl({ value: this.commonService.locationData?.swsParcel || '', disabled: false })
        );
        this.locationDataFG.addControl(
          'swsParcelNumber',
          new FormControl({ value: this.commonService.locationData?.swsParcelNumber || '', disabled: false })
        );
        this.locationDataFG.addControl(
          'swsDevAreaName',
          new FormControl({ value: this.commonService.locationData?.swsDevAreaName || '', disabled: false })
        );

        if (this.commonService.locationData?.hasOwnProperty('noAddressAvailable')) {
          this.updateFormSwsValues(this.commonService.locationData?.noAddressAvailable, false);
        }
      }
    }

    if (this.showConversionOptions) {
      this.locationDataFG.addControl('mediaConversion', new FormControl({ value: false, disabled: false }));
    }

    this.evaluateFormValidity();
    this.locationDataFG.valueChanges.subscribe(() => {
      this.evaluateFormValidity();
      this.jurisdictionCheckFailed = true;
      this.cdr.detectChanges();
    });
    if (this.showNewDevAreaDetails && this.locationDataFG.get('newDevArea')) {
      this.locationDataFG.get('newDevArea').valueChanges.subscribe(newDevArea => {
        if (this.currentInstallationFlow === this.installationFlowType.MainsConnection) {
          this.newBuildingWarning = newDevArea;
          this.jurisdictionService.newDevAreaMessage(this.newBuildingWarning);
        }
      });
    }
    if (this.currentInstallationFlow === this.installationFlowType.MainsConnection) {
      const selectedBuilding = this.localStorageService?.getItem('selected_building_type');
      const selectedBuildingType = selectedBuilding ? selectedBuilding[0].lookUpKey : '';
      this.locationDataFG.addControl(
        'buildingType',
        new FormControl(
          { value: selectedBuildingType, disabled: false },
          this.environmentService.showNewMainsConnectionBuildingType ? [Validators.required] : []
        )
      );
      this.enableLswButton();
    }
    if (this.currentInstallationFlow === InstallationFlowType.UpdateInstallation && this.environmentService.isMeterShutdown) {
      this.locationDataFG.get('powerType').valueChanges.subscribe(power => {
        this.jurisdictionService.setPowerType(power);
      });
    }

    this.configureConnectionTypeSection();

    // restore location
    if (this.commonService.locationData?.location?.position) {
      this.updateLocationValues(
        {
          x: this.commonService.locationData?.location.position?.longitude,
          y: this.commonService.locationData?.location.position?.latitude,
          z: this.environmentService.mapData.gpsCoordinates.z,
        },
        this.commonService.locationData?.location
      );
    }

    if (isRefresh) {
      this.selectMeter();
    }
  }

  consumptionDevicesFormInitiated(consumptionDevicesForm: FormGroup) {
    this.locationDataFG.addControl('consumption_devices', consumptionDevicesForm);
  }

  private configureConnectionTypeSection() {
    if (!this.isUsePowerConnectionTypeForNewMains) return;

    if (this.commonService.gridConnectionApplicationData?.sk_power_connection_type) {
      this.addConnectionTypeCtrl();
      if (!this.locationDataFG.controls.buildingType.value) {
        this.locationDataFG.controls.buildingType.setValue(this.buildingTypes[0].value);
        this.locationDataFG.controls.buildingType.updateValueAndValidity();
      }
      this.hideBuildingTypesForNewMains = this.shouldHideBuildingTypes();
    }

    this.locationDataFG.get('power').valueChanges.subscribe(power => {
      if (power) {
        this.locationDataFG.controls.buildingType.setValue(this.buildingTypes[0].value);
        this.locationDataFG.controls.buildingType.updateValueAndValidity();
        this.addConnectionTypeCtrl();
      } else {
        this.locationDataFG.removeControl('connectionType');
      }
      this.hideBuildingTypesForNewMains = this.shouldHideBuildingTypes();
      this.isUsePowerConnectionTypeForNewMains = power;
    });

    this.locationDataFG.get('water').valueChanges.subscribe(_ => {
      this.hideBuildingTypesForNewMains = this.shouldHideBuildingTypes();
    });
    this.locationDataFG.get('heating').valueChanges.subscribe(_ => {
      this.hideBuildingTypesForNewMains = this.shouldHideBuildingTypes();
    });
    this.locationDataFG.get('gas').valueChanges.subscribe(_ => {
      this.hideBuildingTypesForNewMains = this.shouldHideBuildingTypes();
    });
  }

  private shouldHideBuildingTypes(): boolean {
    if (!this.locationDataFG) {
      return !this.environmentService.showNewMainsConnectionBuildingType;
    }
    return (
      !this.environmentService.showNewMainsConnectionBuildingType ||
      (this.locationDataFG.controls.power?.value &&
        !this.locationDataFG.controls.water?.value &&
        !this.locationDataFG.controls.heating?.value &&
        !this.locationDataFG.controls.gas?.value)
    );
  }

  private addConnectionTypeCtrl(): void {
    this.locationDataFG.addControl(
      'connectionType',
      new FormGroup({
        powerConnectionType: new FormControl(this.commonService.gridConnectionApplicationData?.sk_power_connection_type, [
          Validators.required,
        ]),
      })
    );
  }

  private configureAreaLocation() {
    this.useAreaLocation = this.areaLocations?.length ? true : false;
    this.locationDataFG.addControl(
      'useAreaLocation',
      new FormControl({
        value: this.useAreaLocation,
        disabled: false,
      })
    );
    this.locationDataFG.addControl(
      'area',
      new FormControl({
        value: (this.areaLocations && this.areaLocations[0]?.area) || '',
        disabled: false,
      })
    );
    this.locationDataFG.addControl(
      'field',
      new FormControl({
        value: (this.areaLocations && this.areaLocations[0]?.field) || '',
        disabled: false,
      })
    );
    this.locationDataFG.controls.field.valueChanges.subscribe(value => {
      const areaLocations = this.areaLocations ? this.areaLocations[0] : null;
      this.setAreaLocation({ ...areaLocations, field: value });
    });
    this.locationDataFG.addControl(
      'field_sector',
      new FormControl({
        value:
          (this.areaLocations && this.areaLocations[0]?.field_sector) || (this.areaLocations && this.areaLocations[0]?.fieldSector) || '',
        disabled: false,
      })
    );
    this.locationDataFG.controls.field_sector.valueChanges.subscribe(value => {
      const areaLocations = this.areaLocations ? this.areaLocations[0] : null;
      this.setAreaLocation({ ...areaLocations, field_sector: value });
    });
    this.locationDataFG.addControl('gridCompExternalId', new FormControl(this.commonService.locationData?.gridCompExternalId || ''));
    if (this.useDropdownForArea) {
      this.environmentService.getAreaOptions.then((options: AreaOption[]) => {
        this.areaOptions = options;
        this.selectedArea = this.areaOptions.find(o => o.area === this.locationDataFG.controls.area.value) ?? { area: null, zip: null };
      });
    }
    this.addOtherAreaField();
    if (this.locationDataFG.controls.other_area) {
      this.locationDataFG.controls.other_area.setValue(this.areaLocations?.[0]?.other_area || null);
    }
  }

  private registerForLanguageChange(): void {
    this.netzTranslateService.registerForLanguageChange().subscribe(() => {
      this.setJurisdictionCheckMessage();
    });
  }

  private getExistingApplication(applicationId: string, flow: string, isRefresh = false) {
    let applicationSubscription: Observable<any>;
    if (
      this.currentInstallationFlow === this.installationFlowType.NewInstallation ||
      this.currentInstallationFlow === this.installationFlowType.UpdateInstallation ||
      this.currentInstallationFlow === this.installationFlowType.FeedInSystem ||
      this.currentInstallationFlow === this.installationFlowType.SiteElectricityMeterdata
    ) {
      applicationSubscription = this.commonService
        .getInstallationApplication(applicationId)
        .pipe(map(result => result.installationapplication[0]));
    } else if (
      this.currentInstallationFlow === this.installationFlowType.MainsConnection ||
      this.currentInstallationFlow === this.installationFlowType.TemporaryConnection ||
      this.currentInstallationFlow === this.installationFlowType.ChangeHouseConnection ||
      this.currentInstallationFlow === this.installationFlowType.ConversionToGas
    ) {
      applicationSubscription = this.commonService.getGridConnectionApplication(applicationId).pipe(
        map(result => {
          if (!result.gridconapplication) return null;
          return result.gridconapplication[0];
        })
      );
    } else if (this.currentInstallationFlow === this.installationFlowType.GridCompatibility) {
      applicationSubscription = this.commonService.getGridCompFeedInApplicationById(applicationId).pipe(map(result => result[0]));
    } else if (this.currentInstallationFlow === this.installationFlowType.RegEP) {
      applicationSubscription = this.commonService.getRegepApplication(applicationId);
    } else if (this.currentInstallationFlow === this.installationFlowType.ConsumptionDevices) {
      applicationSubscription = this.commonService.getConsumptionDeviceApplication(applicationId);
    }

    applicationSubscription.subscribe(result => {
      this.application = result;
      this.commonService.locationData = JSON.parse(this.application?.location?.location_object ?? null);
      this.areaLocations = result?.connectionobject.area_locations;
      this.prepareForm(isRefresh);
      this.selectLocationForUpdate();

      if (this.commonService.locationData) {
        this.verifyServiceAvailability();
      }
    });
  }

  private HandleSpecificApplication(): void {
    let flowType: number = this.GetFlowType();

    const applicationId = this.localStorageService.getItem('application_id');

    if (this.application) {
      // if coming from copy request skip the confirmation pop-up
      if (this.isAllowedToUpdateLocationData) {
        if (flowType === 3) {
          this.onGridCompNextStep();
        } else if (flowType === 4) {
          this.onRegepNextStep(this.application);
        }
      } else this.saveAppLocation(applicationId);
      if (flowType === 1) {
        this.updateInstallationApplication();
      }
      this.setSelectedFacilities();
      if (this.selectedFacilityTypes.length) {
        this.resetExistingApplication(this.application as GetInstallationApplicationObject | GetGridConnectionObject);
      } else {
        if (flowType === 1) {
          if (this.isGridCompatibility || this.isRegEP) {
            localStorage.setItem(this.updateApplicationKey, 'true');
          }

          this.commonService.installationApplicationData = this.application as InstallationApplicationData;
          this.handleDataOnRedirection(this.application as InstallationApplicationData | GridConnectionData);
        } else if (flowType === 2) {
          const applicationId = (this.application as GridConnectionData).application_id;
          const requestData: any = {
            ordnance_area: this.showOrdnanceWarning ? '1' : '0',
            sk_power_connection_type: this.locationDataFG.value.connectionType?.powerConnectionType,
          };

          if (this.useAreaLocation) {
            requestData.connectionobject = {
              area_locations: [
                {
                  area: this.locationDataFG.value.area,
                  field: this.locationDataFG.value.field,
                  field_sector: this.locationDataFG.value.field_sector,
                  postcode: this.locationDataFG.value.postcode,
                  other_area: this.locationDataFG.value.other_area,
                },
              ],
            };
          }

          this.commonService.updateGridConnectionApplication(applicationId, requestData).subscribe(
            updateApplicationResponse => {
              this.isLoading = false;
              this.errorService.throwErrorIfFailure(updateApplicationResponse);
              this.commonService.gridConnectionApplicationData = updateApplicationResponse.gridconapplication[0];
              this.handleDataOnRedirection(this.commonService.gridConnectionApplicationData);
            },
            error => {
              this.isLoading = false;
              this.errorService.throwErrorIfFailure(error, true);
            }
          );
        } else if (flowType === 3) {
          this.updateGridCompApplication(() => this.onGridCompNextStep(this.application));
        } else if (flowType === 4) {
          this.updateRegepApplication(() => this.onRegepNextStep(this.application));
        } else if (flowType === 5) {
          this.updateConsumptionDevicesApplication();
        }
      }
    } else {
      this.createNewApplication({ onSuccess: applicationId => this.saveAppLocation(applicationId) });
    }
  }

  private GetFlowType() {
    let flowType: number;
    if (
      this.currentInstallationFlow === this.installationFlowType.NewInstallation ||
      this.currentInstallationFlow === this.installationFlowType.UpdateInstallation ||
      this.currentInstallationFlow === this.installationFlowType.FeedInSystem ||
      this.currentInstallationFlow === this.installationFlowType.SiteElectricityMeterdata
    ) {
      flowType = 1;
    } else if (
      this.currentInstallationFlow === this.installationFlowType.MainsConnection ||
      this.currentInstallationFlow === this.installationFlowType.TemporaryConnection ||
      this.currentInstallationFlow === this.installationFlowType.ChangeHouseConnection ||
      this.currentInstallationFlow === this.installationFlowType.ConversionToGas
    ) {
      flowType = 2;
    } else if (this.currentInstallationFlow === this.installationFlowType.GridCompatibility) {
      flowType = 3;
    } else if (this.currentInstallationFlow === this.installationFlowType.RegEP) {
      flowType = 4;
    } else if (this.currentInstallationFlow === this.installationFlowType.ConsumptionDevices) {
      flowType = 5;
    }

    return flowType;
  }

  // if coming from copy request- skip setting the gridCompFeedInApplicationData as we already have the id setup
  private onGridCompNextStep(response: any = null) {
    if (!this.isAllowedToUpdateLocationData && response) {
      this.commonService.gridCompFeedInApplicationData = response as GridCompFeedInApplicationData;
      this.localStorageService.setItem('application_id', this.commonService.gridCompFeedInApplicationData.application_id);
    }

    localStorage.setItem(this.updateApplicationKey, 'true');
    localStorage.removeItem('gridcomp_update_application');
    this.localStorageService.setItem('location_data', this.locationDataFG.value);
    this.commonService.locationData = this.locationDataFG.value;
    this.router.navigate(['/gridcomp/technical-data']);
    this.stateShare.setFormState(2);
  }

  private onRegepNextStep(response: RegistrationOfEnergyProducerApplication) {
    if (response?.application_id) {
      this.localStorageService.setItem('application_id', response.application_id);
    }
    localStorage.setItem(this.updateApplicationKey, 'true');
    localStorage.removeItem('gridcomp_update_application');
    this.localStorageService.setItem('location_data', this.locationDataFG.value);
    this.commonService.locationData = this.locationDataFG.value;
    this.router.navigate(['/regep/technical-data']);
    this.stateShare.setFormState(2);
  }

  private createNewApplication(options: { onSuccess: any }): void {
    if (this.currentInstallationFlow === this.installationFlowType.RegEP) {
      return this.createRegepApplication(options?.onSuccess);
    }

    if (this.currentInstallationFlow === this.installationFlowType.GridCompatibility) {
      return this.createGridCompApplication(options?.onSuccess);
    }

    if (
      this.currentInstallationFlow === this.installationFlowType.NewInstallation ||
      this.currentInstallationFlow === this.installationFlowType.UpdateInstallation ||
      this.currentInstallationFlow === this.installationFlowType.FeedInSystem ||
      this.currentInstallationFlow === this.installationFlowType.SiteElectricityMeterdata
    ) {
      return this.createInstallationApplication(options?.onSuccess);
    }

    if (this.currentInstallationFlow === this.installationFlowType.ConsumptionDevices) {
      this.createConsumptionDevicesApplication();
      return;
    }

    return this.createGridConnectionApplication(options?.onSuccess);
  }

  private saveAppLocation(applicationId: string) {
    let location: AppLocation = {
      application_id: applicationId,
      id: this.application?.location?.id ?? 0,
      location_object: JSON.stringify(this.locationDataFG.value),
    };

    if (!location.id) {
      this.commonService.createAppLocation(location).subscribe();
    } else {
      this.commonService.updateAppLocation(location).subscribe();
    }
  }

  private createInstallationApplication(onSuccess: any): void {
    this.isLoading = true;
    this.commonService.createInstallationApplication(this.currentInstallationFlow, this.locationDataFG.value).subscribe(
      (data: CreateInstallationApplicationResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(data, true);
        if (onSuccess) {
          onSuccess(data.installationapplication[0].application_id);
        }
        this.commonService.installationApplicationData = null;
        this.handleDataOnRedirection(data.installationapplication[0]);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private updateInstallationApplication(): void {
    this.isLoading = true;
    const requestModel = {
      applicationId: this.application.application_id,
      ...this.locationDataFG.value,
    };
    this.commonService.updateInstallationApplication(this.currentInstallationFlow, requestModel).subscribe(
      (data: CreateInstallationApplicationResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(data, true);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private createGridConnectionApplication(onSuccess: any): void {
    const switchDefaultValue = this.environmentService.setDefaultToggleStates ? true : false;
    this.isLoading = true;
    const requestData: CreateGridConnectionRequest = {
      sk_gridconapplication_type: this.commonService.getGridConnectionApplicationType(this.currentInstallationFlow),
      change_power: this.locationDataFG.value.power ? '1' : '0',
      change_gas: this.locationDataFG.value.gas || this.locationDataFG.value.conversionType === 'conversion_gas' ? '1' : '0',
      change_water: this.locationDataFG.value.water ? '1' : '0',
      change_heat: this.locationDataFG.value.heating || this.locationDataFG.value.conversionType === 'conversion_heat' ? '1' : '0',
      ordnance_area: this.showOrdnanceWarning ? '1' : '0',
      automatic_offer: this.environmentService.createAutomaticOffer ? '1' : '0',
      notify_on_submit: this.environmentService.getNotificationOnSubmitRequest,
      applicant_is_invoicerecipient: switchDefaultValue ? '1' : '0',
      applicant_is_landowner: switchDefaultValue ? '1' : '0',
    };

    if (this.currentInstallationFlow === InstallationFlowType.MainsConnection && !this.isConversionToGas) {
      requestData.sk_building_type = this.locationDataFG.value.buildingType;
      requestData.development_area = this.locationDataFG.value.newDevArea ? '1' : '0';
      if (this.isUsePowerConnectionTypeForNewMains) {
        requestData.sk_power_connection_type = this.locationDataFG.value.connectionType?.powerConnectionType;
      }
    } else if (this.currentInstallationFlow === InstallationFlowType.ChangeHouseConnection) {
      requestData.sk_change_subtype = this.locationDataFG.value.connectionModification;
    } else if (this.currentInstallationFlow === InstallationFlowType.ConversionToGas) {
      requestData.sk_change_subtype = this.locationDataFG.value.conversionType;
    }

    if (this.showConversionOptions && this.isConversionToGas) {
      requestData.sk_gridconapplication_type = GridConnectionApplicationType.Change;
    }

    this.localStorageService.setItem('location_data', this.locationDataFG.value);

    requestData.connectionobject = requestData.connectionobject || {};
    if (this.useAreaLocation) {
      requestData.connectionobject.area_locations = [
        {
          area: this.locationDataFG.value.area,
          field: this.locationDataFG.value.field,
          field_sector: this.locationDataFG.value.field_sector,
          postcode: this.locationDataFG.value.postcode,
          other_area: this.locationDataFG.value.other_area,
        },
      ];
    }

    this.commonService.createGridConnectionApplication(requestData).subscribe(
      (data: CreateGridConnectionResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(data, true);
        if (onSuccess) {
          onSuccess(data.gridconapplication[0].application_id);
        }
        this.commonService.gridConnectionApplicationData = null;
        this.handleDataOnRedirection(data.gridconapplication[0] as GridConnectionData);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private buildGridAppUpdateRequest(): any {
    const request: any = {
      application_id: this.application?.application_id,
      connectionobject: {
        area_locations: [
          {
            area: this.locationDataFG.value.area,
            field: this.locationDataFG.value.field,
            field_sector: this.locationDataFG.value.field_sector,
            postcode: this.locationDataFG.value.postcode,
            other_area: this.locationDataFG.value.other_area,
          },
        ].filter(l => l.area && l.field && l.field_sector),
      },
      grid_compatibility_order_number: this.locationDataFG.value.gridCompExternalId,
    };

    if (this.isRegEP && this.locationDataFG.value.totalFeedInPowerIsAboveCentralGridThreshold) {
      request.connectionobject = null;
    }

    return request;
  }

  private buildConsumptionDeviceRequest(): any {
    return {
      application_id: this.application?.application_id,
      connectionobject: {
        area_locations: [
          {
            area: this.locationDataFG.value.area,
            field: this.locationDataFG.value.field,
            field_sector: this.locationDataFG.value.field_sector,
            postcode: this.locationDataFG.value.postcode,
            other_area: this.locationDataFG.value.other_area,
          },
        ].filter(l => l.area && l.field && l.field_sector),
      },
      is_registration_device: this.locationDataFG.value.consumption_devices.consumption_device_type === 'drr',
      is_consumption_device: this.locationDataFG.value.consumption_devices.consumption_device_type === 'ccd',
    };
  }

  private createGridCompApplication(onSuccess: any): void {
    const request = this.buildGridAppUpdateRequest();
    this.isLoading = true;
    this.commonService.createGridCompApplication(request).subscribe(
      (data: CreateGridCompFeedInApplicationResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(data, true);
        if (onSuccess) {
          onSuccess(data);
        }
        this.commonService.gridCompFeedInApplicationData = null;
        this.localStorageService.setItem('application_id', data);
        this.localStorageService.setItem('location_data', this.locationDataFG.value);
        this.commonService.locationData = this.locationDataFG.value;
        this.router.navigate(['/gridcomp/technical-data']);
        this.stateShare.setFormState(2);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }
  private updateGridCompApplication(onSuccess: any): void {
    const request = this.buildGridAppUpdateRequest();
    this.isLoading = true;
    this.commonService.updateGridCompApplication(request).subscribe(
      (data: CreateGridCompFeedInApplicationResponse) => {
        this.isLoading = false;
        if (onSuccess) {
          onSuccess(data);
        }
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private createRegepApplication(onSuccess: any) {
    const request = this.buildGridAppUpdateRequest();
    this.isLoading = true;
    this.commonService.createRegEPApplication(request).subscribe(
      (regepApplicationId: number) => {
        this.errorService.throwErrorIfFailure(regepApplicationId, true);

        if (onSuccess) {
          onSuccess(regepApplicationId);
        }

        this.localStorageService.setItem('application_id', regepApplicationId.toString());
        this.localStorageService.setItem('location_data', this.locationDataFG.value);

        this.commonService.locationData = this.locationDataFG.value;

        this.router.navigate(['/regep/technical-data']);
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      },
      () => (this.isLoading = false)
    );
  }

  private updateRegepApplication(onSuccess: any) {
    this.isLoading = true;
    const request = this.buildGridAppUpdateRequest();

    this.commonService.updateRegEPApplication(request, this.application.application_id).subscribe(
      (regepApplicationId: number) => {
        this.errorService.throwErrorIfFailure(regepApplicationId, true);
        if (onSuccess) {
          onSuccess();
        }
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      },
      () => (this.isLoading = false)
    );
  }

  private updateConsumptionDevicesApplication() {
    const request = this.buildConsumptionDeviceRequest();
    this.isLoading = true;
    this.drrCcdDeviceService.updateDrrCcdDeviceApplication(request).subscribe(
      (response: any) => {
        this.errorService.throwErrorIfFailure(request.application_id, true);
        const applicationId = response.application_id;

        this.localStorageService.setItem('application_id', applicationId.toString());
        this.localStorageService.setItem('location_data', this.locationDataFG.value);

        this.commonService.locationData = this.locationDataFG.value;

        this.router.navigate(['drr-ccd-devices/technical-data'], {
          queryParams: {
            subType: this.locationDataFG.get('consumption_devices')?.get('consumption_device_type')?.value,
          },
        });
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      },
      () => (this.isLoading = false)
    );
  }

  private createConsumptionDevicesApplication(onSuccess?: (id: number) => {}) {
    const request = this.buildConsumptionDeviceRequest();
    this.isLoading = true;
    this.drrCcdDeviceService.createDrrCcdDeviceApplication(request).subscribe(
      (applicationId: any) => {
        this.errorService.throwErrorIfFailure(applicationId, true);

        if (onSuccess) {
          onSuccess(applicationId);
        }

        this.localStorageService.setItem('application_id', applicationId.toString());
        this.localStorageService.setItem('location_data', this.locationDataFG.value);

        this.commonService.locationData = this.locationDataFG.value;

        this.router.navigate(['drr-ccd-devices/technical-data'], {
          queryParams: {
            subType: this.locationDataFG.get('consumption_devices')?.get('consumption_device_type')?.value,
          },
        });
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      },
      () => (this.isLoading = false)
    );
  }

  private handleDataOnRedirection(data: any): void {
    this.setSelectedFacilities();
    if (this.selectedFacilityTypes.length) {
      this.localStorageService.setItem('selected_facilities', this.selectedFacilityTypes);
    }
    if (this.currentInstallationFlow === InstallationFlowType.MainsConnection) {
      const selectedBuildingType = TechnicianFormType.filter(item => item.lookUpKey === this.locationDataFG.value.buildingType);
      this.localStorageService.setItem('selected_building_type', selectedBuildingType);
    }
    if (this.currentInstallationFlow === InstallationFlowType.ChangeHouseConnection) {
      this.localStorageService.setItem('selected_change_type', this.locationDataFG.value.connectionModification);
    } else if (this.currentInstallationFlow === InstallationFlowType.ConversionToGas) {
      this.localStorageService.setItem('selected_change_type', this.locationDataFG.value.conversionType);
    }
    this.localStorageService.setItem('location_data', this.locationDataFG.value);
    this.commonService.locationData = this.locationDataFG.value;

    if (data) {
      const applicationId = (data as { application_id: any }).application_id;

      this.localStorageService.setItem('application_id', applicationId);
    }
    if (this.currentInstallationFlow === InstallationFlowType.FeedInSystem) {
      const feedInInstallationData: FeedInInstallationDataObject = (data as InstallationApplicationData).feedininstallation;
      if (feedInInstallationData && feedInInstallationData.installation_id) {
        this.localStorageService.setItem('installation_id', feedInInstallationData.installation_id);
        this.localStorageService.setItem('constructor_id', feedInInstallationData.constructor.constructor_id);
        this.localStorageService.setItem('installer_id', feedInInstallationData.installer.installer_id);
      }
    }

    // TODO: add new component for site-electricity-and-meter-data flow.
    if (
      this.currentInstallationFlow === InstallationFlowType.MainsConnection ||
      this.currentInstallationFlow === InstallationFlowType.TemporaryConnection ||
      this.currentInstallationFlow === InstallationFlowType.ChangeHouseConnection ||
      this.currentInstallationFlow === InstallationFlowType.ConversionToGas
    ) {
      this.router.navigate(['/technician-form']);
    } else if (this.currentInstallationFlow === InstallationFlowType.FeedInSystem) {
      this.router.navigate(['/technical-data']);
    } else if (this.currentInstallationFlow === InstallationFlowType.SiteElectricityMeterdata) {
      this.router.navigate(['/site-electricity-technical-data']);
    } else {
      if (this.environmentService.showTechnicalFormFirst) {
        this.router.navigate(['/facilities-data']);
      } else {
        this.router.navigate(['/personal-data']);
      }
    }
    this.stateShare.setFormState(2);
  }

  private setJurisdictionCheckMessage(): void {
    if (this.jurisdictionCheckFailed && this.failedJurisdictionChecks.length) {
      this.jurisdictionCheckMessage = this.netzTranslateService.translateString('basic-profile.jurisdiction.jurisdiction-check-message');
      this.failedJurisdictionChecks.forEach((key, index) => {
        this.jurisdictionCheckMessage +=
          index === 0
            ? ' ' + this.netzTranslateService.translateString(`overview.details-data.data-rows.${key}`)
            : ', ' + this.netzTranslateService.translateString(`overview.details-data.data-rows.${key}`);
      });
    } else {
      this.jurisdictionCheckMessage = '';
    }
  }

  private getTechnicianData(): void {
    this.isLoading = true;
    this.communicationService.getData(`technician?technician_id=${this.technicianId}`).subscribe(
      (response: TechnicianResponseData) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        const technicianData = (response.technician && response.technician[0]) || null;
        this.commonService.technicianData = technicianData;
        this.mediumVisibility = this.commonService.getPermittedTradesData(technicianData);
        this.hideCommodityWater ? (this.mediumVisibility.water = false) : '';
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private resetExistingApplication(applicationData: any): void {
    const isInstallationFlow =
      this.currentInstallationFlow === this.installationFlowType.NewInstallation ||
      this.currentInstallationFlow === this.installationFlowType.UpdateInstallation ||
      this.currentInstallationFlow === this.installationFlowType.FeedInSystem ||
      this.currentInstallationFlow === this.installationFlowType.SiteElectricityMeterdata;
    const applicationId = applicationData.application_id;
    if (this.selectedFacilityTypes.length) {
      if (!isInstallationFlow && applicationId) {
        let requestData: any = {
          change_gas: '0',
          change_heat: '0',
          change_power: '0',
          change_water: '0',
          ordnance_area: this.showOrdnanceWarning ? '1' : '0',
          sk_power_connection_type: this.locationDataFG.value.connectionType?.powerConnectionType,
        };
        FacilityFormType.forEach(item => {
          const selectedFacility = this.selectedFacilityTypes.filter(type => type.key === item.key);
          if (selectedFacility.length) {
            switch (item.key) {
              case 'gas':
                requestData.change_gas = '1';
                break;
              case 'heating':
                requestData.change_heat = '1';
                break;
              case 'power':
                requestData.change_power = '1';
                break;
              case 'water':
                requestData.change_water = '1';
                break;
            }
          }
        });

        requestData.connectionobject = requestData.connectionobject || {};

        if (this.useAreaLocation) {
          requestData.connectionobject.area_locations = [
            {
              area: this.locationDataFG.value.area,
              field: this.locationDataFG.value.field,
              field_sector: this.locationDataFG.value.field_sector,
              postcode: this.locationDataFG.value.postcode,
              other_area: this.locationDataFG.value.other_area,
            },
          ];
        }

        this.localStorageService.setItem('location_data', this.locationDataFG.value);

        this.isLoading = true;
        this.commonService.updateGridConnectionApplication(applicationId, requestData).subscribe(
          updateApplicationResponse => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(updateApplicationResponse);
            this.resetApplicationSubsections(isInstallationFlow, applicationData);
          },
          error => {
            this.isLoading = false;
            this.errorService.throwErrorIfFailure(error, true);
          }
        );
      } else {
        this.resetApplicationSubsections(isInstallationFlow, applicationData);
      }
    }
  }

  private resetApplicationSubsections(
    isInstallationFlow: boolean,
    applicationData: InstallationApplicationData | GridConnectionData
  ): void {
    let subscriptionCount = -1;
    FacilityFormType.forEach(item => {
      const selectedFacility = this.selectedFacilityTypes.filter(type => type.key === item.key);
      let installationId = '';
      let urlSegment = '';

      switch (item.key) {
        case 'power':
          // if (!selectedFacility.length) {
          installationId = isInstallationFlow
            ? (applicationData as InstallationApplicationData).powerinstallation &&
              (applicationData as InstallationApplicationData).powerinstallation.installation_id
            : (applicationData as GridConnectionData).powergridcon && (applicationData as GridConnectionData).powergridcon.gridcon_id;
          urlSegment = isInstallationFlow
            ? `${UrlSegments.UpdatePowerInstallation}=${installationId}`
            : `${UrlSegments.UpdatePowerGridConnection}${installationId}`;
          // }
          break;
        case 'gas':
          // if (!selectedFacility.length) {
          installationId = isInstallationFlow
            ? (applicationData as InstallationApplicationData).gasinstallation &&
              (applicationData as InstallationApplicationData).gasinstallation.installation_id
            : (applicationData as GridConnectionData).gasgridcon && (applicationData as GridConnectionData).gasgridcon.gridcon_id;
          urlSegment = isInstallationFlow
            ? `${UrlSegments.UpdateGasInstallation}=${installationId}`
            : `${UrlSegments.UpdateGasGridConnection}${installationId}`;
          // }
          break;
        case 'water':
          // if (!selectedFacility.length) {
          installationId = isInstallationFlow
            ? (applicationData as InstallationApplicationData).waterinstallation &&
              (applicationData as InstallationApplicationData).waterinstallation.installation_id
            : (applicationData as GridConnectionData).watergridcon && (applicationData as GridConnectionData).watergridcon.gridcon_id;
          urlSegment = isInstallationFlow
            ? `${UrlSegments.UpdateWaterInstallation}=${installationId}`
            : `${UrlSegments.UpdateWaterGridConnection}${installationId}`;
          // }
          break;
        case 'heating':
          // if (!selectedFacility.length) {
          installationId = isInstallationFlow
            ? (applicationData as InstallationApplicationData).districtheatinstallation &&
              (applicationData as InstallationApplicationData).districtheatinstallation.installation_id
            : (applicationData as GridConnectionData).districtheatgridcon &&
              (applicationData as GridConnectionData).districtheatgridcon.gridcon_id;
          urlSegment = isInstallationFlow
            ? `${UrlSegments.UpdateHeatingInstallation}=${installationId}`
            : `${UrlSegments.UpdateHeatingGridConnection}${installationId}`;
          // }
          break;
      }
      if (installationId) {
        if (!selectedFacility.length) {
          subscriptionCount = subscriptionCount === -1 ? 1 : subscriptionCount + 1;
          this.isLoading = true;
          this.commonService.deleteMediaObjectsFromApplication(urlSegment).subscribe(
            applicationResponse => {
              this.isLoading = false;
              subscriptionCount -= 1;
              this.errorService.throwErrorIfFailure(applicationResponse, true);

              if (subscriptionCount === 0) {
                this.isLoading = true;
                const subscription: Observable<GetInstallationApplicationObject | GetGridConnectionObject> = isInstallationFlow
                  ? this.commonService.getExistingInstallationApplications(this.currentInstallationFlow)
                  : this.commonService.getExistingGridConnectionApplication(this.currentInstallationFlow);

                subscription.subscribe(
                  (response: GetInstallationApplicationObject | GetGridConnectionObject) => {
                    this.isLoading = false;
                    this.errorService.throwErrorIfFailure(response, true);
                    if (
                      response &&
                      (response as GetInstallationApplicationObject).installationapplication &&
                      (response as GetInstallationApplicationObject).installationapplication.length
                    ) {
                      this.commonService.installationApplicationData = (
                        response as GetInstallationApplicationObject
                      ).installationapplication[0];
                    } else if (
                      response &&
                      (response as GetGridConnectionObject).gridconapplication &&
                      (response as GetGridConnectionObject).gridconapplication.length
                    ) {
                      this.commonService.gridConnectionApplicationData = (response as GetGridConnectionObject).gridconapplication[0];
                    }
                    this.handleDataOnRedirection(this.commonService.gridConnectionApplicationData);
                  },
                  error => {
                    this.isLoading = false;
                    this.errorService.throwErrorIfFailure(error, true);
                  }
                );
              }
            },
            error => {
              this.isLoading = false;
              this.errorService.throwErrorIfFailure(error, true);
            }
          );
        } else {
          let requestObject = {};
          this.isLoading = true;
          if (this.currentInstallationFlow === this.installationFlowType.UpdateInstallation) {
            if (item.key === 'power') {
              requestObject = {
                sk_inst_change_type: this.locationDataFG.value.powerType,
              };
            } else if (item.key === 'gas') {
              requestObject = {
                sk_inst_change_type_gas: this.locationDataFG.value.gasType,
              };
            } else if (item.key === 'water') {
              requestObject = {
                sk_inst_change_type_water: this.locationDataFG.value.waterType,
              };
            } else if (item.key === 'heating') {
              requestObject = {
                sk_inst_change_type_districtheat: this.locationDataFG.value.heatingType,
              };
            }
            this.commonService.updateMediaObjectsFromApplication(urlSegment, requestObject).subscribe(
              response => {
                this.isLoading = true;
                this.errorService.throwErrorIfFailure(response, true);
                this.commonService.getExistingInstallationApplications(this.currentInstallationFlow).subscribe(
                  applicationResponse => {
                    this.isLoading = false;
                    if (
                      applicationResponse &&
                      (applicationResponse as GetInstallationApplicationObject).installationapplication &&
                      (applicationResponse as GetInstallationApplicationObject).installationapplication.length
                    ) {
                      this.commonService.installationApplicationData = (
                        applicationResponse as GetInstallationApplicationObject
                      ).installationapplication[0];
                    }
                    this.handleDataOnRedirection((applicationResponse as GetInstallationApplicationObject).installationapplication[0]);
                  },
                  error => {
                    this.isLoading = false;
                    this.errorService.throwErrorIfFailure(error, true);
                  }
                );
              },
              error => {
                this.isLoading = false;
                this.errorService.throwErrorIfFailure(error, true);
              }
            );
          }
        }
      }
    });
    if (subscriptionCount === -1) {
      if (isInstallationFlow) {
        this.commonService.installationApplicationData = applicationData as InstallationApplicationData;
      } else {
        this.commonService.gridConnectionApplicationData = applicationData as GridConnectionData;
      }
      this.handleDataOnRedirection(applicationData);
    }
  }

  private setSelectedFacilities(): void {
    this.selectedFacilityTypes = [];
    FacilityFormType.forEach(item => {
      if (this.locationDataFG.value[camelCase(item.key)]) {
        this.selectedFacilityTypes.push(item);
      }
    });

    if (this.selectedFacilityTypes.length === 0) {
      if (this.currentInstallationFlow === this.installationFlowType.ConversionToGas) {
        this.selectedFacilityTypes =
          FacilityFormType.filter(item => {
            if (
              (this.locationDataFG.value.conversionType === 'conversion_gas' && item.key === 'gas') ||
              (this.locationDataFG.value.conversionType === 'conversion_heat' && item.key === 'heating')
            ) {
              return item;
            }
          }) || [];
      }
    }
  }

  private handleValidators(clearValidators: boolean = false) {
    if (clearValidators) {
      this.locationDataFG.controls.postcode?.reset();
      this.locationDataFG.controls.city?.reset();
      this.locationDataFG.controls.street?.reset();
      this.locationDataFG.controls.street_number?.reset();
      this.locationDataFG.controls.district?.reset();
      this.locationDataFG.controls.location?.reset();

      this.locationDataFG.controls.postcode?.clearValidators();
      this.locationDataFG.controls.city?.clearValidators();
      this.locationDataFG.controls.street?.clearValidators();
      this.locationDataFG.controls.street_number?.clearValidators();
      this.locationDataFG.controls.district?.clearValidators();
      this.locationDataFG.controls.location?.clearValidators();
    } else {
      this.locationDataFG.controls.postcode?.setValidators([Validators.required, ValidationsService.maxLengthValidator()]);
      this.locationDataFG.controls.city?.setValidators(Validators.required);
      this.locationDataFG.controls.street?.setValidators(Validators.required);
      this.locationDataFG.controls.location?.setValidators(Validators.required);
      this.locationDataFG.controls.street_number?.setValidators([Validators.required, ValidationsService.streetNumberValidator()]);
    }

    this.locationDataFG.controls.postcode?.updateValueAndValidity();
    this.locationDataFG.controls.city?.updateValueAndValidity();
    this.locationDataFG.controls.street?.updateValueAndValidity();
    this.locationDataFG.controls.street_number?.updateValueAndValidity();
    this.locationDataFG.controls.district?.updateValueAndValidity();
    this.locationDataFG.controls.location?.updateValueAndValidity();
  }

  private checkJurisdiction(
    facilityType: string
  ): Observable<{ facilityType: string; successfulJurisdictionCheck: boolean; ordnanceArea: boolean }> {
    const facilityTypeToMediumMap = {
      power: 'power',
      gas: 'gas',
      water: 'water',
      heating: 'districtheat',
    };
    const medium = facilityTypeToMediumMap[facilityType];

    const requestData: CheckServiceAvailabilityData = {
      city: this.locationDataFG.value.city,
      district: this.locationDataFG.value.district,
      medium,
      street: this.locationDataFG.value.street,
      street_number: this.locationDataFG.value.street_number,
      x: this.gpsCoordinates.x,
      y: this.gpsCoordinates.y,
      zipcode: this.locationDataFG.value.postcode,
    };

    if (this.showNewDevAreaDropdownForConnection || this.showNewDevAreaDropdownForInstallation) {
      requestData.development_area = this.locationDataFG.value.newDevArea ? '1' : '0';
      requestData.development_area_name = this.devAreaTypes.find(area => area.value === this.locationDataFG.value.devAreaName)?.label;
      requestData.sk_development_area_type = this.locationDataFG.value.devAreaName;
    }

    return this.jurisdictionService.checkServiceAvailability(requestData).pipe(
      map(response => {
        this.errorService.throwErrorIfFailure(response, true);
        const result = response.regionalstructure[0];

        return {
          facilityType,
          successfulJurisdictionCheck: result.result,
          ordnanceArea: result.ordnancearea,
        };
      })
    );
  }
}
